import { FC, ReactElement, useEffect, useState } from 'react'
import styled from 'styled-components'
import { device } from '../breakpoints'
import { VscClose } from 'react-icons/vsc'
import { MdOutlineKeyboardArrowDown } from 'react-icons/md'

interface FiltersProps {
  data: Person[]
  show: boolean
  onFilterChange: (filters: any) => void
}

// Get the list of unique mainSector from the data
const getValues = (key: keyof Person, data: Person[]) => {
  const mainSectors = new Set()

  data.forEach((item) => {
    mainSectors.add(item[key])
  })

  // return an array of objects with the value and label properties
  return Array.from(mainSectors)
    .map((mainSector: any) => {
      return { value: mainSector || 'Autre', label: mainSector || 'Autre' }
    })
    .sort((a: any, b: any) => {
      return a.label.localeCompare(b.label)
    })
}

// Get the list of unique residence from the data
const getResidences = (data: Person[]) => {
  const residences = new Set()

  data.forEach((item) => {
    item.residence.forEach((residence: string) => {
      residences.add(residence)
    })
  })

  // return an array of objects with the value and label properties
  return Array.from(residences)
    .map((mainSector: any) => {
      return { value: mainSector || 'Autre', label: mainSector || 'Autre' }
    })
    .sort((a: any, b: any) => {
      return a.label.localeCompare(b.label)
    })
}

const getFortunes = () => {
  return [
    { label: "Plus de 10 milliards", value: [10000, 100000] },
    { label: "Plus d'un milliard", value: [1000, 10000] },
    { label: '900 millions à 1 milliard', value: [900, 1000] },
    { label: '800 à 900 millions', value: [800, 900] },
    { label: '700 à 800 millions', value: [700, 800] },
    { label: '600 à 700 millions', value: [600, 700] },
    { label: '500 à 600 millions', value: [500, 600] },
    { label: '400 à 500 millions', value: [400, 500] },
    { label: '300 à 400 millions', value: [300, 400] },
    { label: '200 à 300 millions', value: [200, 300] },
  ]
}

const fortuneEvolution: { [key: string]: string } = {
  new: 'Nouveau',
  up: 'En hausse',
  down: 'En baisse',
  flat: 'Stable',
}

const Filters: FC<FiltersProps> = ({ data, show, onFilterChange }): ReactElement => {
  const [filters, setFilters] = useState<any>(new Map())

  const onSelectChange = (value: any, filterName: string) => {
    const newFilters = new Map(filters)

    if (value) {
      if (!newFilters.has(value.label)) {
        newFilters.set(
          filterName,
          value.value
        )
        setFilters(newFilters)
        return
      }
    } else {
      newFilters.delete(filterName)
      setFilters(newFilters)
      return
    }
  }

  useEffect(() => {
    // console.log('filters changed')
    onFilterChange(filters)
  }, [filters])

  return (
    <Container style={{maxHeight: show ? '500px' : '0px', opacity: show ? 1 : 0}}>
      <Content>
      <Select
        onSelectChange={(v: any, n: any) => {
          onSelectChange(v, n)
        }}
        name={'residence'}
        placeholder='Résidence'
        options={getResidences(data)}
      />

      <Select
        onSelectChange={(v: any, n: any) => {
          onSelectChange(v, n)
        }}
        name={'fortune'}
        placeholder='Fortune'
        options={getFortunes()}
      />

      <Select
        onSelectChange={(v: any, n: any) => {
          onSelectChange(v, n)
        }}
        name={'mainSector'}
        placeholder='Secteur(s)'
        options={getValues('mainSector', data)}
      />

      <Select
        onSelectChange={(v: any, n: any) => {
          onSelectChange(v, n)
        }}
        name={'fortuneEvolution'}
        placeholder='Évolution'
        options={getValues('fortuneEvolution', data).map((f) => ({
          value: f.value,
          label: fortuneEvolution[f.label],
        }))}
      />
      </Content>
    </Container>
  )
}

const Container = styled.div`
  font-family: var(--font-sans-serif);
  font-color: ${({ theme }) => theme.blue};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;
  box-sizing: border-box;
`

const Content = styled.div`
  margin-top: 20px;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    column-count: 1;
    padding: 0 15px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    column-count: 4;
    padding: 0;
  }
`

const SelectContainer = styled.div`
  position: relative;
  font-family: var(--font-sans-serif);
  font-size: 1.6rem;
  margin-bottom: 15px;
  color: ${({ theme }) => theme.blue};
`

const CustomSelect = styled.select<{ placeholderSelected: boolean }>`
  // A reset of styles, including removing the default dropdown arrow
  appearance: none;
  // Additional resets for further consistency
  background-color: #33333311;
  border: none;
  padding: 0 1em 0 0;
  margin: 0;
  width: 100%;
  font-family: inherit;
  font-size: inherit;
  cursor: inherit;
  line-height: inherit;
  outline: none;

  font-family: var(--font-sans-serif);
  font-size: 1.6rem;
  width: 100%;
  min-width: 15ch;
  padding: 15px 20px;
  cursor: pointer;
  line-height: 1.1;
  background-color: ${({ theme }) => theme.buttonColor};
  color: ${({ theme, placeholderSelected }) => theme.blue};

  &:-ms-expand {
    display: none;
  }
`

const ResetFilter = styled.div`
  position: absolute;
  top: 0;
  right: 42px;
  width: 40px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 2.2rem;
  background-color: ${({ theme }) => theme.buttonColor};

  &:after {
    content: '';
    width: 1px;
    height: 50%;
    background-color: rgba(0, 0, 0, 0.05);
    position: absolute;
    top: 0;
    transform: translateY(50%);
    right: 0;
  }
`

const ArrowContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 50px;
  font-size: 2.2rem;
  cursor: pointer;
  pointer-events: none;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.buttonColor};
`

export default Filters

const Select = ({
  onSelectChange,
  name,
  options,
  placeholder,
}: {
  onSelectChange: (v: any, l: any) => void
  name: string
  options: any
  placeholder: string
}) => {
  const [value, setValue] = useState<any>('placeholder')

  const onChange = (value: string | null) => {
    setValue(value)
  }

  return (
    <SelectContainer>
      <CustomSelect
        value={value}
        placeholderSelected={value === 'placeholder'}
        onChange={(e: any) => {
          onChange(e.target.value)
          onSelectChange({ value: e.target.value, label: e.target.text }, name)
        }}
      >
        <option value='placeholder' disabled>
          {placeholder}
        </option>
        {options.map((o: any) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </CustomSelect>
      {value !== 'placeholder' && (
        <ResetFilter
          onClick={() => {
            onChange('placeholder')
            onSelectChange(null, name)
          }}
        >
          <VscClose />
        </ResetFilter>
      )}
      <ArrowContainer>
        <MdOutlineKeyboardArrowDown />
      </ArrowContainer>
    </SelectContainer>
  )
}
